/**
 * The navigation model.
 *
 * A nav item appears only when BOTH are true:
 *
 *   1. the MODULE is enabled — `GET /api/platform/navigation` reflects what the admin
 *      switched on in the module configurator. A disabled module is hidden from
 *      everybody, super-admin included, because that is a deployment decision.
 *   2. the USER holds at least one of the item's permissions.
 *
 * Items the user cannot use are HIDDEN, not disabled. A greyed-out "Payroll" tab tells
 * 380 people that a payroll module exists and they are not trusted with it; it generates
 * support tickets and tells them nothing useful. Permission checks here are convenience
 * only — every route re-checks server side.
 *
 * `moduleKey` matches `ModuleConfig.moduleKey`. An item with `moduleKey: null` is part of
 * the shell (dashboard, my inbox) and is never module-gated.
 *
 * WRITE THE SCOPE YOU MEAN.
 *
 * `hasAny(['hr.employee.read'])` is TRUE for somebody holding only `hr.employee.read.own`,
 * because the scope ladder treats a request with no scope as "any scope will do". That is
 * correct for a control acting on ONE record — the employee's own profile — and wrong for a
 * top-level destination that lists OTHER PEOPLE.
 *
 * The consequence, measured against the seeded demo roles: a plain employee (36 permissions)
 * and a manager (62) rendered an IDENTICAL top-level sidebar — Employees, Payroll, Projects
 * and all — because the employee holds `hr.employee.read.own`, `payroll.payslip.read.own` and
 * `projects.read.own`. Every one of those links then refused at the route. Sub-navigation was
 * already right because those entries name the scope; the top level did not.
 *
 * So: an item whose PAGE is about other people names `.team` or wider. An item whose page is
 * genuinely the user's own work (my timesheet, my leave, my payslips) names `.own` and stays
 * visible to everybody. Where a section mixes both, the section is visible on the narrowest
 * entitlement and its `to` resolves to the first child the user can actually open —
 * `landingFor` below — so nobody is sent to a page that will refuse them.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Banknote,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  ClipboardList,
  Clock,
  Cog,
  FileBarChart,
  Handshake,
  FileSpreadsheet,
  Inbox,
  LayoutDashboard,
  Layers,
  Receipt,
  ShieldCheck,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** `null` for shell items that are never module-gated. */
  moduleKey: string | null;
  /** Any one of these grants visibility. Empty = visible to every signed-in user. */
  permissions: string[];
  /** Sub-items shown when the section is active. */
  children?: { label: string; to: string; permissions?: string[] }[];
  /** Renders the pending-approvals count. */
  badge?: 'approvals' | 'notifications';
}

export interface NavSection {
  key: string;
  label: string | null;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main',
    label: null,
    items: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        to: '/',
        icon: LayoutDashboard,
        moduleKey: null,
        permissions: [],
      },
      {
        key: 'inbox',
        label: 'My approvals',
        to: '/approvals',
        icon: Inbox,
        moduleKey: null,
        // Deciding, not merely watching. Somebody who can only read their OWN instances has
        // nothing to act on here — their requests are shown on the module pages instead.
        permissions: ['workflows.instance.approve', 'workflows.instance.read.team'],
        badge: 'approvals',
      },
    ],
  },
  {
    key: 'me',
    label: 'My work',
    items: [
      {
        key: 'timesheets',
        label: 'Timesheets',
        to: '/timesheets',
        icon: Clock,
        moduleKey: 'timesheets',
        // Genuinely own work — visible to everybody who books time.
        permissions: ['timesheets.read.own', 'timesheets.edit.own'],
        children: [
          { label: 'My timesheet', to: '/timesheets' },
          { label: 'History', to: '/timesheets/history' },
          { label: 'Approvals', to: '/timesheets/approvals', permissions: ['timesheets.approve'] },
          // Locking is what makes a period an immutable input to payroll and billing. It
          // had no screen at all, so nothing in the product could close a period.
          { label: 'Payroll lock', to: '/timesheets/payroll-lock', permissions: ['timesheets.lockPeriod'] },
          // `timesheets.report.read` does not exist in the catalogue, so this entry was
          // invisible to everyone but a super-admin and the route behind it unreachable.
          { label: 'Reports', to: '/timesheets/reports', permissions: ['timesheets.read.team'] },
        ],
      },
      {
        key: 'leave',
        label: 'Leave',
        to: '/leave',
        icon: CalendarDays,
        moduleKey: 'leave',
        permissions: ['leave.request.read.own', 'leave.request.submit'],
        children: [
          { label: 'My leave', to: '/leave' },
          { label: 'Request leave', to: '/leave/request', permissions: ['leave.request.submit'] },
          { label: 'Team calendar', to: '/leave/calendar', permissions: ['leave.request.read.team'] },
          { label: 'Approvals', to: '/leave/approvals', permissions: ['leave.request.approve'] },
          { label: 'Holidays', to: '/leave/holidays', permissions: ['leave.holiday.read'] },
        ],
      },
      {
        key: 'expenses',
        label: 'Expenses',
        to: '/expenses',
        icon: Receipt,
        moduleKey: 'spending',
        permissions: ['spending.expense.read.own', 'spending.expense.submit'],
        children: [
          { label: 'My expenses', to: '/expenses' },
          { label: 'Approvals', to: '/expenses/approvals', permissions: ['spending.expense.approve'] },
          { label: 'Purchase orders', to: '/purchase-orders', permissions: ['spending.po.read'] },
          { label: 'Vendors', to: '/vendors', permissions: ['spending.vendor.read'] },
          { label: 'Analytics', to: '/expenses/analytics', permissions: ['spending.analytics.read'] },
        ],
      },
    ],
  },
  {
    key: 'people',
    label: 'People',
    items: [
      {
        key: 'employees',
        label: 'Employees',
        to: '/employees',
        icon: Users,
        moduleKey: 'hr',
        // `.team` and not bare `.read`: the directory lists OTHER PEOPLE. With `.own` the
        // API narrows the result to the signed-in employee, so the "directory" was a
        // one-row list of yourself — visible to all 380 people and useful to none.
        permissions: ['hr.employee.read.team'],
        children: [
          { label: 'Directory', to: '/employees' },
          { label: 'Org chart', to: '/employees/org-chart', permissions: ['hr.orgChart.read'] },
        ],
      },
      {
        key: 'onboarding',
        label: 'Onboarding',
        to: '/onboarding',
        icon: ClipboardList,
        moduleKey: 'onboarding',
        permissions: ['onboarding.checklist.read'],
        children: [
          { label: 'My checklist', to: '/onboarding' },
          { label: 'Active', to: '/onboarding/active', permissions: ['onboarding.checklist.read.all'] },
          { label: 'Offboarding', to: '/onboarding/offboarding', permissions: ['onboarding.offboarding.start'] },
          { label: 'Templates', to: '/onboarding/templates', permissions: ['onboarding.template.read'] },
        ],
      },
      {
        key: 'agencies',
        label: 'Agencies',
        to: '/agencies',
        icon: Handshake,
        moduleKey: 'hr',
        permissions: ['hr.agency.read'],
        children: [
          { label: 'Agencies', to: '/agencies' },
          { label: 'Placements', to: '/agencies/placements' },
        ],
      },
      {
        key: 'work-auth',
        label: 'Work authorisation',
        to: '/work-auth',
        icon: ShieldCheck,
        moduleKey: 'workAuth',
        // Was `workAuth.record.read`, which is not a permission this product has, so the
        // item was dead for everyone. The dashboard route requires `workAuth.alert.read`.
        permissions: ['workAuth.alert.read'],
      },
      {
        key: 'payroll',
        label: 'Payroll',
        to: '/payroll',
        icon: Banknote,
        moduleKey: 'payroll',
        // Everybody who is paid has payslips; only payroll staff have runs. The section is
        // therefore visible on the narrow entitlement and `landingFor` sends each user to
        // the first child they can actually open — `/payroll` refuses `payslip.read.own`.
        permissions: ['payroll.run.read', 'payroll.payslip.read.own'],
        children: [
          { label: 'Runs', to: '/payroll', permissions: ['payroll.run.read'] },
          { label: 'My payslips', to: '/payroll/payslips' },
          { label: 'Tax tables', to: '/payroll/tax-tables', permissions: ['payroll.taxTable.read'] },
          { label: 'Filings', to: '/payroll/filings', permissions: ['payroll.filing.read'] },
        ],
      },
    ],
  },
  {
    key: 'delivery',
    label: 'Delivery',
    items: [
      {
        key: 'projects',
        label: 'Projects',
        to: '/projects',
        icon: BriefcaseBusiness,
        moduleKey: 'projects',
        // `.own` deliberately: the list narrows to the engagements the person is staffed on,
        // which is a page they can open and that tells them something. Unlike the directory,
        // it is not a list of other people.
        permissions: ['projects.read.own'],
        children: [
          { label: 'All projects', to: '/projects' },
          { label: 'Profitability', to: '/projects/profitability', permissions: ['projects.financials.read'] },
        ],
      },
      {
        key: 'clients',
        label: 'Clients',
        to: '/clients',
        icon: Building2,
        moduleKey: 'projects',
        permissions: ['projects.client.read'],
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    items: [
      {
        key: 'invoices',
        label: 'Invoices & AR',
        to: '/finance/invoices',
        icon: FileSpreadsheet,
        moduleKey: 'finance',
        permissions: ['finance.invoice.read'],
        children: [
          { label: 'Invoices', to: '/finance/invoices' },
          { label: 'AR ageing', to: '/finance/ar-ageing' },
          { label: 'Payments', to: '/finance/payments', permissions: ['finance.payment.read'] },
        ],
      },
      {
        key: 'payables',
        label: 'Bills & AP',
        to: '/finance/bills',
        icon: Wallet,
        moduleKey: 'finance',
        permissions: ['finance.bill.read'],
        children: [
          { label: 'Bills', to: '/finance/bills' },
          { label: 'AP ageing', to: '/finance/ap-ageing' },
        ],
      },
      {
        key: 'ledger',
        label: 'General ledger',
        to: '/finance/journal',
        icon: Layers,
        moduleKey: 'finance',
        permissions: ['finance.journal.read', 'finance.account.read'],
        children: [
          { label: 'Journal', to: '/finance/journal', permissions: ['finance.journal.read'] },
          { label: 'Chart of accounts', to: '/finance/accounts', permissions: ['finance.account.read'] },
          { label: 'Periods', to: '/finance/periods', permissions: ['finance.period.close'] },
          { label: 'Budgets', to: '/finance/budgets', permissions: ['finance.budget.read'] },
        ],
      },
      {
        key: 'reports',
        label: 'Reports',
        to: '/reports',
        icon: FileBarChart,
        moduleKey: 'reporting',
        permissions: ['finance.report.read', 'timesheets.read.team', 'spending.analytics.read'],
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    items: [
      {
        key: 'workflows',
        label: 'Workflows',
        to: '/admin/workflows',
        icon: Workflow,
        moduleKey: 'workflows',
        permissions: ['workflows.template.read'],
      },
      {
        key: 'settings',
        label: 'Settings',
        to: '/admin',
        icon: Cog,
        moduleKey: null,
        permissions: [
          'admin.role.read',
          'admin.permission.read',
          'platform.policy.read',
          'platform.lookup.read',
          'platform.formSchema.read',
          'platform.module.manage',
          'notifications.template.read',
          'rules.read',
          'jobArchitecture.levelFramework.read',
        ],
        children: [
          { label: 'Roles & permissions', to: '/admin/roles', permissions: ['admin.role.read'] },
          { label: 'Policies', to: '/admin/policies', permissions: ['platform.policy.read'] },
          { label: 'Lookups', to: '/admin/lookups', permissions: ['platform.lookup.read'] },
          { label: 'Form designer', to: '/admin/forms', permissions: ['platform.formSchema.read'] },
          { label: 'Modules', to: '/admin/modules', permissions: ['platform.navigation.read'] },
          {
            label: 'Job architecture',
            to: '/admin/job-architecture',
            permissions: ['jobArchitecture.levelFramework.read'],
          },
          // The overview COUNTED job families, job codes and career paths and offered no way
          // to create any of them — the most frustrating shape a gap can take.
          {
            label: 'Job catalogue',
            to: '/admin/job-architecture/catalogue',
            permissions: ['jobArchitecture.jobFamily.read'],
          },
          {
            label: 'Career paths',
            to: '/admin/job-architecture/career-paths',
            permissions: ['jobArchitecture.careerPath.read'],
          },
          {
            label: 'Notification templates',
            to: '/admin/notification-templates',
            permissions: ['notifications.template.read'],
          },
          { label: 'Business rules', to: '/admin/rules', permissions: ['rules.read'] },
          // Documents was built with a route and a page and NO WAY TO REACH IT — an admin had
          // to type the URL. Exactly the defect verify-reachable exists to catch, committed
          // while writing the document about it. A route is not a feature; a link is.
          {
            label: 'Documents',
            to: '/admin/documents',
            permissions: ['onboarding.storage.manage'],
          },
          {
            label: 'Single sign-on',
            to: '/admin/sso',
            permissions: ['admin.settings.manage'],
          },
          {
            label: 'Email delivery',
            to: '/admin/email',
            permissions: ['admin.settings.manage'],
          },
        ],
      },
    ],
  },
];

/**
 * Where a section's own label should link.
 *
 * The declared `to` is the canonical landing page, but it is not always one this user can
 * open: Payroll lands on Runs, and somebody who only has `payroll.payslip.read.own` gets a
 * 403 there while "My payslips" sits one line below, working. Rather than hide a section a
 * user has legitimate business in, the label follows the first child they may open.
 */
function landingFor(item: NavItem, visibleChildren: NavItem['children'], hasAny: (p: readonly string[]) => boolean): string {
  const declaredChild = item.children?.find((child) => child.to === item.to);
  const declaredIsOpenable =
    declaredChild === undefined ||
    declaredChild.permissions === undefined ||
    hasAny(declaredChild.permissions);
  if (declaredIsOpenable) return item.to;
  return visibleChildren?.[0]?.to ?? item.to;
}

/** Filters the model down to what this user, in this deployment, may actually see. */
export function visibleSections(
  hasAny: (permissions: readonly string[]) => boolean,
  enabledModules: ReadonlySet<string> | null,
): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => {
        // `null` modules are shell items. When the navigation endpoint is unreadable
        // (a baseline user lacks `platform.navigation.read`) we fall back to permissions
        // alone rather than hiding the whole app.
        if (item.moduleKey === null || enabledModules === null) return true;
        return enabledModules.has(item.moduleKey);
      })
      .filter((item) => item.permissions.length === 0 || hasAny(item.permissions))
      .map((item) => {
        const children = item.children?.filter(
          (child) => child.permissions === undefined || hasAny(child.permissions),
        );
        return {
          ...item,
          to: landingFor(item, children, hasAny),
          ...(children === undefined ? {} : { children }),
        };
      }),
  })).filter((section) => section.items.length > 0);
}
